import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../state';

/**
 * ReviewBanner — the "Help the next inventor find us" testimonial ask,
 * shared across surfaces so its dismiss / already-reviewed state is unified.
 *
 * Two variants of one component (same copy, same ask, figure/ground inverted):
 *  - `band` — a filled dark heartwood band for the dashboard, where the goal
 *    is raw visibility. Fades in on mount.
 *  - `coda` — a flat, on-light closing note for the very bottom of a completed
 *    memo page, where the surrounding aesthetic is flat-on-parchment and the
 *    ask should feel earned, not shout. Reveals on scroll into view, sits
 *    below a hairline rule.
 *
 * Cross-surface state (localStorage):
 *  - `completed` — set once the user clicks through to the form; hides the ask
 *    everywhere, permanently. We never re-ask someone who already vouched.
 *  - `dismissedAt` / `dismissCount` — a dismissal snoozes the ask everywhere,
 *    for an escalating interval (1 → 3 → 7 → 14 → 45 days) that grows with how
 *    many times they've dismissed, so repeated "not now" is respected.
 *  - `memoShownAt` — coda-only: caps the memo-page ask to at most once per
 *    14 days, so a prolific inventor compiling several memos isn't re-asked
 *    on every one.
 *
 * `enabled` carries the caller's context gating (dashboard: engaged inventor +
 * no active survey banner; memo: Go/Pivot verdict + the post-memo survey didn't
 * appear this session). The component owns everything else.
 */

const SENJA_FORM = 'https://senja.io/p/entredash/r/TJU4cP';
const COMPLETED_KEY = 'entredash.reviewBanner.completed';
const DISMISSED_KEY = 'entredash.reviewBanner.dismissedAt';
const DISMISS_COUNT_KEY = 'entredash.reviewBanner.dismissCount';
const MEMO_SHOWN_KEY = 'entredash.reviewBanner.memoShownAt';
const DAY_MS = 24 * 60 * 60 * 1000;
// Escalating snooze: each dismissal backs the ask off longer than the last.
// A casual "not now" brings it back the next day; someone who keeps closing
// it is progressively left alone (1 → 3 → 7 → 14 → 45 days, then holds at 45).
const SNOOZE_SCHEDULE_DAYS = [1, 3, 7, 14, 45];
const MEMO_CAP_MS = 14 * DAY_MS; // at most once / 14 days on memo pages

/** How long the Nth dismissal snoozes for (1-indexed; holds at the last step). */
function snoozeMsForDismissCount(count: number): number {
  const idx = Math.min(Math.max(count, 1), SNOOZE_SCHEDULE_DAYS.length) - 1;
  return SNOOZE_SCHEDULE_DAYS[idx] * DAY_MS;
}

/** Cross-surface "hide it" check: completed forever, or within the snooze. */
function reviewHidden(): boolean {
  try {
    if (localStorage.getItem(COMPLETED_KEY) === 'true') return true;
    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) ?? 0);
    if (dismissedAt <= 0) return false;
    // The snooze length escalates with how many times they've dismissed.
    const count = Number(localStorage.getItem(DISMISS_COUNT_KEY) ?? 1);
    return Date.now() - dismissedAt < snoozeMsForDismissCount(count);
  } catch {
    return false;
  }
}

/**
 * Build the Senja collection-form URL with the signed-in user's name and
 * email prefilled. Shared so the banner, the coda, and the always-available
 * account-menu entry all point at the exact same prefilled form.
 */
export function buildReviewUrl(
  profile: { name?: string | null; email?: string | null } | null | undefined,
): string {
  return `${SENJA_FORM}?name=${encodeURIComponent(
    profile?.name ?? '',
  )}&email=${encodeURIComponent(profile?.email ?? '')}`;
}

/**
 * Mark the review ask as satisfied so the prompt (band + coda) stops showing
 * everywhere. Called both when the user clicks through the banner and when
 * they use the permanent account-menu entry — either way they've gone to
 * leave a review, so we shouldn't keep prompting. The menu entry itself stays
 * available regardless, so the ask is never lost.
 */
export function markReviewCompleted(): void {
  try {
    localStorage.setItem(COMPLETED_KEY, 'true');
  } catch {
    /* ignore */
  }
}

export function ReviewBanner({
  variant,
  enabled = true,
}: {
  variant: 'band' | 'coda';
  enabled?: boolean;
}) {
  const profile = useAuth((s) => s.profile);
  const [hidden, setHidden] = useState(reviewHidden);
  const [leaving, setLeaving] = useState(false);
  // Band fades in on mount; coda reveals on scroll into view.
  const [revealed, setRevealed] = useState(variant === 'band');
  const ref = useRef<HTMLDivElement | null>(null);

  // Coda-only 14-day per-page cap, decided once so it's stable across renders.
  const [codaAllowed] = useState(() => {
    if (variant !== 'coda') return true;
    try {
      const shownAt = Number(localStorage.getItem(MEMO_SHOWN_KEY) ?? 0);
      return !(shownAt > 0 && Date.now() - shownAt < MEMO_CAP_MS);
    } catch {
      return true;
    }
  });

  const willShow = enabled && !hidden && codaAllowed && !!profile;

  // Stamp the per-page cap the first time the coda actually commits to showing.
  useEffect(() => {
    if (variant === 'coda' && willShow) {
      try {
        localStorage.setItem(MEMO_SHOWN_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    }
  }, [variant, willShow]);

  // Scroll reveal for the coda — it should arrive as the reader finishes the
  // runway above it, not be pre-faded before they scroll down.
  useEffect(() => {
    if (variant !== 'coda' || !willShow || revealed) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRevealed(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [variant, willShow, revealed]);

  if (!willShow) return null;

  const senjaUrl = buildReviewUrl(profile);

  // A dismissal snoozes the ask everywhere (escalating with each dismissal),
  // then collapses smoothly.
  const dismiss = () => {
    try {
      const nextCount =
        Number(localStorage.getItem(DISMISS_COUNT_KEY) ?? 0) + 1;
      localStorage.setItem(DISMISS_COUNT_KEY, String(nextCount));
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* localStorage unavailable — dismissal just won't persist */
    }
    setLeaving(true);
    setTimeout(() => setHidden(true), 320);
  };
  // Clicking through means they're giving us a testimonial — never ask again.
  const complete = markReviewCompleted;

  const isCoda = variant === 'coda';

  return (
    <div
      ref={ref}
      className={[
        'rvb-wrap',
        isCoda ? 'rvb-wrap--coda' : '',
        leaving ? 'is-dismissing' : '',
        isCoda && revealed ? 'is-revealed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="rvb-clip">
        {isCoda && <div className="rvb-rule" aria-hidden="true" />}
        <div className={`rvb ${isCoda ? 'rvb--coda' : 'rvb--band'}`}>
          <span className="rvb__medallion" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 9h8" />
              <path d="M8 13h3.5" />
              <path d="M10.48 19.512l-2.48 1.488v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v4" />
              <path d="M18 22l3.35 -3.284a2.143 2.143 0 0 0 .005 -3.071a2.242 2.242 0 0 0 -3.129 -.006l-.224 .22l-.223 -.22a2.242 2.242 0 0 0 -3.128 -.006a2.143 2.143 0 0 0 -.006 3.071l3.355 3.296" />
            </svg>
          </span>
          <div className="rvb__body">
            <div className="rvb__eyebrow">Quick favor</div>
            <div className="rvb__title">Help the next inventor find us</div>
            <div className="rvb__subtitle">
              If EntreDash has helped you get somewhere, a few honest words from
              you could be what convinces the next inventor to start.
            </div>
          </div>
          <a
            className="rvb__cta"
            href={senjaUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={complete}
          >
            Tell your story
            <svg
              className="rvb__arrow"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12l14 0" />
              <path d="M13 18l6 -6" />
              <path d="M13 6l6 6" />
            </svg>
          </a>
          <button className="rvb__dismiss" aria-label="Dismiss" onClick={dismiss}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6l-12 12" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <style>{reviewBannerStyles}</style>
    </div>
  );
}

const reviewBannerStyles = `
  /* Collapsing wrapper — shared by both variants. Dismiss animates the grid
     row to 0fr so content below slides up rather than snapping. */
  .rvb-wrap {
    display: grid;
    grid-template-rows: 1fr;
    transition:
      grid-template-rows 0.28s cubic-bezier(0.16, 1, 0.3, 1),
      opacity 0.2s ease,
      margin 0.28s ease;
  }
  .rvb-wrap:not(.rvb-wrap--coda) { margin-top: 28px; }
  .rvb-wrap > .rvb-clip { overflow: hidden; min-height: 0; }
  .rvb-wrap.is-dismissing {
    grid-template-rows: 0fr;
    opacity: 0;
    margin-top: 0;
  }

  .rvb {
    position: relative;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  /* ---- band variant (dark, dashboard) ---- */
  .rvb--band {
    padding: 22px 26px;
    border-radius: var(--r-md);
    overflow: hidden;
    isolation: isolate;
    background:
      radial-gradient(78% 130% at 93% -18%, oklch(0.68 0.13 158 / 0.30), transparent 56%),
      linear-gradient(158deg, #204535 0%, #1b3a2e 60%, #163027 100%);
    box-shadow:
      0 1px 2px rgba(15, 23, 20, 0.05),
      0 14px 30px -18px rgba(20, 44, 34, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 0 0 1px rgba(244, 239, 226, 0.08);
    animation: rvb-in 0.42s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes rvb-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
  }

  /* ---- coda variant (flat, on-light, memo page) ---- */
  .rvb-wrap--coda { margin-bottom: 8px; }
  .rvb-rule {
    height: 1px;
    background: var(--border-hairline);
    margin: 40px 0 28px;
  }
  .rvb--coda {
    gap: 24px;
    /* no fill, radius, or shadow — this is not a card */
    opacity: 0;
    transform: translateY(8px);
    transition:
      opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .rvb-wrap--coda.is-revealed .rvb--coda {
    opacity: 1;
    transform: none;
  }

  /* ---- medallion ---- */
  .rvb__medallion {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: grid;
    place-items: center;
  }
  .rvb__medallion svg { width: 21px; height: 21px; }
  .rvb--band .rvb__medallion {
    color: #3fbe82;
    background: rgba(244, 239, 226, 0.09);
    box-shadow: inset 0 0 0 1px rgba(244, 239, 226, 0.16);
  }
  .rvb--coda .rvb__medallion {
    color: #1f9d63;
    background: rgba(27, 58, 46, 0.06);
    box-shadow: inset 0 0 0 1px rgba(27, 58, 46, 0.14);
  }

  /* ---- type ---- */
  .rvb__body { flex: 1; min-width: 0; }
  .rvb__eyebrow {
    font-family: var(--f-mono);
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-bottom: 7px;
  }
  .rvb--band .rvb__eyebrow { color: rgba(244, 239, 226, 0.6); }
  .rvb--coda .rvb__eyebrow { color: #1b3a2e; }
  .rvb__title {
    font-family: var(--f-serif);
    font-size: 19px;
    font-weight: 500;
    line-height: 1.24;
    letter-spacing: -0.006em;
    margin-bottom: 5px;
  }
  .rvb--band .rvb__title { color: #f6f2e6; }
  .rvb--coda .rvb__title { color: var(--text-primary); }
  .rvb__subtitle {
    font-size: 13.5px;
    line-height: 1.5;
    max-width: 54ch;
  }
  .rvb--band .rvb__subtitle { color: rgba(244, 239, 226, 0.66); }
  .rvb--coda .rvb__subtitle { color: var(--text-decorative); }

  /* ---- CTA ---- */
  .rvb__cta {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: none;
    cursor: pointer;
    border-radius: 8px;
    text-decoration: none;
    white-space: nowrap;
    font-family: 'Switzer', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: 0.01em;
    transition:
      transform 0.16s cubic-bezier(0.16, 1, 0.3, 1),
      background 0.16s ease,
      box-shadow 0.16s ease;
  }
  .rvb--band .rvb__cta {
    background: #f4efe2;
    color: #1b3a2e;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  }
  .rvb--band .rvb__cta:hover {
    background: #fbf8f0;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -6px rgba(0, 0, 0, 0.35);
  }
  .rvb--coda .rvb__cta {
    background: #1b3a2e;
    color: #f4efe2;
    box-shadow: 0 1px 2px rgba(15, 23, 20, 0.1);
  }
  .rvb--coda .rvb__cta:hover {
    background: #204535;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -8px rgba(27, 58, 46, 0.45);
  }
  .rvb__cta:active { transform: translateY(0); }
  .rvb__arrow { width: 16px; height: 16px; transition: transform 0.16s ease; }
  .rvb__cta:hover .rvb__arrow { transform: translateX(2px); }

  /* ---- dismiss ---- */
  .rvb__dismiss {
    flex-shrink: 0;
    align-self: flex-start;
    margin: -6px -8px 0 -4px;
    width: 26px;
    height: 26px;
    display: grid;
    place-items: center;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: color 0.14s ease, background 0.14s ease;
  }
  .rvb__dismiss svg { width: 15px; height: 15px; }
  .rvb--band .rvb__dismiss { color: rgba(244, 239, 226, 0.42); }
  .rvb--band .rvb__dismiss:hover {
    color: rgba(244, 239, 226, 0.82);
    background: rgba(244, 239, 226, 0.08);
  }
  .rvb--coda .rvb__dismiss { color: rgba(15, 23, 20, 0.32); }
  .rvb--coda .rvb__dismiss:hover {
    color: rgba(15, 23, 20, 0.6);
    background: rgba(15, 23, 20, 0.05);
  }

  /* ---- responsive ---- */
  @media (max-width: 600px) {
    .rvb { flex-wrap: wrap; gap: 16px; }
    .rvb--band { padding: 20px; }
    .rvb__medallion { order: 1; }
    .rvb__body { order: 2; flex-basis: 100%; }
    .rvb__cta { order: 3; width: 100%; justify-content: center; }
    .rvb__dismiss { position: absolute; top: 12px; right: 12px; margin: 0; align-self: auto; }
    .rvb--coda .rvb__dismiss { top: 0; right: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .rvb--band { animation: none; }
    .rvb--coda { transition: none; opacity: 1; transform: none; }
    .rvb-wrap { transition: none; }
    .rvb__cta, .rvb__arrow, .rvb__dismiss { transition: none; }
  }
`;
